/**
 * AlurackSolarPlanner — Wrapper bridging the pv-layout-core SolarPlanner 3D
 * widget with the Alurack Redux store and REST API.
 *
 * Mirrors the pattern of Sunwise's SunwiseSolarPlanner but:
 *  - Removes irradiation / Google-Irradiation-API features (Alurack doesn't use them)
 *  - Uses `onSegmentDrawRequest` to open Alurack's AddSegmentFormContainer
 *    BEFORE drawing starts and then forwards the collected settings via
 *    `startDraw({ initialSettings, drawingMode })`.
 *  - Forwards Alurack's extra fields (sloof_type, gclamp_type, system_uid,
 *    category, preset_configuration, rail_spacing, rail_support_length,
 *    plate_support_length, beam_spacing, support_length, lfoot_supports_width,
 *    outer_rail_length, front_base_height, type_application) through the
 *    library's `roofEditExtra` render prop so the user can tweak them after
 *    creation, and any geometry-changing edit is persisted via
 *    `onSegmentUpdate`. Note: `category` is Alurack's storage key for the
 *    terrain/land category (see LandCategoryWithTooltip).
 */

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { SolarPlanner, useSolarStore } from '@sunwisesoftware/pv-layout-core';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import { I18nextProvider } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { createStructuredSelector } from 'reselect';

import { filterSystemTypesList } from 'common/api/v1/core';
import { patchItem } from 'common/api/v1/panelModules';
import { filterPanelsList } from 'common/api/v1/panelsCatalog';
import { patchGroup } from 'common/api/v1/segments';
import { updateImage } from 'common/api/v1/segmentsImage';
import {
    createSegment as apiCreateSegment,
    deleteSegment as apiDeleteSegment,
    updateSegment as apiUpdateSegment,
} from 'common/api/v1/segmentsLayout';
import {
    getPolygonHeadings,
    setCeilZone,
} from 'common/components/segment/asceHelpers';
import {
    POLYGON_OBSTACLE,
    POLYGON_RECTANGLE,
    POLYGON_SEGMENT,
} from 'common/components/segment/constants';
import {
    formatSegment,
    getModulesGroupV2,
    markGroupEdges,
    prepareModulesGroups,
} from 'common/components/segment/helpers';
import { getNewSolarModules } from 'common/utils/helpers';
import { getIsoCodeCurrentLocale } from 'common/utils/helpers/multiregion';
import { getOrientationViaSystemUid } from 'common/utils/helpers/systemsValues';

import * as actions from '../actions';
import * as selectors from '../selectors';

import AlurackPanelTabExtra from './AlurackPanelTabExtra';
import AlurackRoofEditExtra from './AlurackRoofEditExtra';
import { alurackToLibrary } from './alurackToLibrary';
import {
    buildSegmentsMap,
    libraryPanelTogglesToAlurack,
    librarySegmentToAlurack,
} from './libraryToAlurack';
import PreDrawFormBridge from './PreDrawFormBridge';

const DEFAULT_COLOR = '#8c8c8c';
const DEFAULT_STROKE = '#11EFD9';

// Alurack-specific fields that are stored in roofSettingsById but do NOT
// affect panel geometry / layout. Marking them as non-geometry prevents
// updateRoofSetting from unnecessarily clearing savedRoofsById (which holds
// pre-computed panel positions) when the user only edits structural metadata.
const ALURACK_NON_GEOMETRY_KEYS = [
    'beam_spacing',
    'brandName',
    // building_heading is metadata only — it does not affect panel layout
    // (azimuthDeg is the geometry key that does). We store it so ASCE
    // calculations can use it and it round-trips via custom_inputs.
    'building_heading',
    'category',
    'fill_algorithm',
    'front_base_height',
    'gclamp_type',
    'is_custom_module',
    'lfoot_supports_width',
    'outer_rail_length',
    'panel_name',
    'panel_weight',
    'plate_support_length',
    'preset_configuration',
    'rail_spacing',
    'rail_support_length',
    'seam_distance',
    'segment_errors',
    'sheet_gauge',
    'sloof_type',
    'support_length',
    'system',
    'system_name',
    'system_uid',
    'type_application',
];

function AlurackSolarPlanner({
    isDisabledEdition,
    isEdition,
    match,
    offerPanels,
    parentFieldSegment,
    presetConfiguration,
    segments,
    setAddSegmentModalOpenStatus,
    setDrawingMode,
    setAddSegmentModalEditionMode,
    updateSegments,
    viewMode = '3d',
}) {
    const commercialOfferId = match?.params?.uid;
    const location = parentFieldSegment?.location;
    const parentFieldSegmentId = parentFieldSegment?.id;
    const isEditionModeActive = isEdition.isEditionMode;

    // Mutable ref — callbacks need the latest segments without re-subscribing.
    const segmentsRef = useRef(segments);
    useEffect(() => {
        segmentsRef.current = segments;
    }, [segments]);

    // ---- Sync segment_errors from Redux into the library store ----------
    // The backend re-computes validations on every save and returns updated
    // `segment_errors`. The library owns segment state after mount, so
    // changes from Redux don't reach SegmentSummary unless we explicitly
    // forward them. Diff against the last seen value to avoid spamming the
    // store; segment_errors is in ALURACK_NON_GEOMETRY_KEYS so this won't
    // invalidate savedRoofsById and trigger a panel re-layout.
    const lastSegmentErrorsRef = useRef(new Map());
    useEffect(() => {
        const store = useSolarStore.getState();
        if (!store.roofSettingsById) return;
        for (const seg of segments || []) {
            const isRoof =
                seg.type === POLYGON_SEGMENT || seg.type === POLYGON_RECTANGLE;
            if (!isRoof) continue;
            const id = seg.id;
            // Skip roofs the library doesn't know about yet (initialProject
            // not hydrated, or this segment was just created and the create
            // request is in flight).
            if (!store.roofSettingsById[id]) continue;

            const next = seg.segment_errors ?? null;
            const prev = lastSegmentErrorsRef.current.get(id);
            // JSON compare is fine here — segment_errors is small and the
            // backend tends to return stable shapes.
            const changed = JSON.stringify(prev) !== JSON.stringify(next);
            if (!changed) continue;

            lastSegmentErrorsRef.current.set(id, next);
            const currentSettings = store.roofSettingsById[id] ?? {};
            // Only push when the stored value would actually change.
            if (
                JSON.stringify(currentSettings.segment_errors ?? null) ===
                JSON.stringify(next)
            ) {
                continue;
            }
            store.updateRoofSetting(
                id,
                { segment_errors: next },
                currentSettings,
                ALURACK_NON_GEOMETRY_KEYS,
            );
        }
    }, [segments]);

    // Track the pending draw request so the existing AddSegmentFormContainer
    // can pick up where it left off once the user confirms the form.
    const pendingDrawRef = useRef(null);
    const pendingEditRef = useRef(null);

    const onSegmentDrawRequest = useCallback(
        ({ drawingType, startDraw }) => {
            if (!startDraw) return;
            setAddSegmentModalEditionMode({
                isEditionMode: false,
                editionData: {},
            });
            pendingDrawRef.current = { drawingType, startDraw };
            setAddSegmentModalOpenStatus(true);
        },
        [setAddSegmentModalEditionMode, setAddSegmentModalOpenStatus],
    );

    const onSegmentEditRequest = useCallback(
        ({ segment, settings, onComplete }) => {
            const alurackSegment = segmentsRef.current.find(
                (s) => s.id === segment.id,
            );
            // Build editionData from both sources:
            //   - custom_inputs: snake_case data from the last API save
            //   - settings: camelCase data from Zustand (most recent, may be
            //     ahead of the API if the user edited without saving the segment)
            //
            // Spread settings AFTER custom_inputs so Zustand values win.
            // AddSegmentFormContainer.initialValues already maps camelCase keys
            // (buildingHeight → building_height, roofSlopeDeg → ceil_tilt, etc.)
            // so having both representations in editionData is safe.
            const customInputs = alurackSegment?.custom_inputs ?? {};
            setAddSegmentModalEditionMode({
                isEditionMode: true,
                editionData: {
                    ...customInputs,
                    ...settings,
                },
                // Intentionally no segmentId here.
                // AddSegmentFormContainer uses segmentId to decide whether to
                // call updateSegmentSettings (API). Without it, the form calls
                // setNewSegmentConfig instead, which PreDrawFormBridge picks up
                // and relays to handlePreDrawFormSubmit → Zustand update.
                // The actual API save happens later via onSegmentUpdate when the
                // user clicks Save in the library sidebar.
            });
            pendingEditRef.current = { segment, settings, onComplete };
            setAddSegmentModalOpenStatus(true);
        },
        [setAddSegmentModalEditionMode, setAddSegmentModalOpenStatus],
    );

    // Normalize the offer panels to the library's shape.
    const libraryPanelOffer = useMemo(() => {
        const source = Array.isArray(offerPanels)
            ? offerPanels
            : offerPanels?.data || [];
        return source.map((p) => ({
            depth: p.depth,
            height: p.height,
            name: p.name,
            panel_company_branch_office: p.id,
            quantity: p.quantity,
            watts: p.watts ?? p.max_power,
            width: p.width,
        }));
    }, [offerPanels]);

    // Build the initial project only on mount — once the widget owns the
    // state we feed callbacks back into Redux but never re-derive the project
    // (doing so would throw away in-flight widget state).
    const initialProject = useMemo(() => {
        if (!location) return null;
        return alurackToLibrary({
            segments,
            location,
            offerPanels: libraryPanelOffer,
            // Pass the proposal's default panel as a guaranteed fallback for
            // panel dimensions. Without this, any segment whose solar_module
            // UUID is absent from offerPanels (e.g. the panel was changed on
            // the proposal after the segment was drawn, or the getLayouts
            // response serializer omits the inline panel object) ends up with
            // settings.panelWidth = undefined. The library's panelLayout()
            // then computes Math.max(0.1, NaN) = NaN and places zero panels,
            // so panels_layout = [] → field_segments.solar_modules = [] →
            // panel_modules is never created → BOM tables are empty.
            proposalPanel: parentFieldSegment?.proposal?.panel ?? null,
        });
    }, []);

    const context = useMemo(
        () => ({
            commercialOfferId,
            parentFieldSegmentId,
            defaultSolarModuleId:
                libraryPanelOffer?.[0]?.panel_company_branch_office ?? null,
            presetConfiguration: presetConfiguration ?? null,
            systemId: parentFieldSegment?.proposal?.system?.id ?? null,
        }),
        [
            commercialOfferId,
            parentFieldSegmentId,
            libraryPanelOffer,
            parentFieldSegment,
            presetConfiguration,
        ],
    );

    const handlePreDrawFormSubmit = useCallback(
        ({ newSegmentConfig, drawingMode }) => {
            // Use pendingEditRef as discriminator, NOT isEditionModeActive.
            // By the time PreDrawFormBridge calls this handler, React has already
            // processed the batched Redux updates from AddSegmentFormContainer
            // (which resets isEditionMode to false), so the closure value of
            // isEditionModeActive would already be stale/false.
            // pendingEditRef is a mutable ref that is reliably set by
            // onSegmentEditRequest and cleared here.
            if (pendingEditRef.current) {
                // Edition path — "Editar Estructura" was accepted.
                // Sync the submitted values into the library's Zustand store so:
                //   1. The next onSegmentUpdate (library Save) sends the updated
                //      values to the API instead of stale creation-time settings.
                //   2. Reopening the form shows the new values (the library passes
                //      the up-to-date roofSettingsById entry as `settings`).
                const pending = pendingEditRef.current;

                const newSettings = _newSegmentConfigToInitialSettings(
                    newSegmentConfig,
                    parentFieldSegment,
                );

                // Do NOT override ANY panel-related field the user may have
                // configured in the library sidebar's Module tab.
                // _newSegmentConfigToInitialSettings always seeds these from the
                // proposal's default panel (defaultPanelSeeds) — correct for a
                // new segment, but WRONG for a structural edit: it would
                // overwrite brandName (and the rest) in the store even though
                // AlurackPanelTabExtra is the sole owner of these fields.
                //
                // Without this cleanup the store ends up with the default
                // panel's brand while selectedPanelCode still points to the
                // panel the user actually picked (a different brand), causing
                // a backend validation error on Save and, when the user is not
                // on the Module tab at edit-time, a silent brand swap that is
                // only visible after switching back to that tab.
                const PANEL_SEED_KEYS = [
                    'selectedPanelCode',
                    'brandName',
                    'panel_name',
                    'panelWidth',
                    'panelHeight',
                    'panelDepth',
                    'panelWatts',
                    'panel_weight',
                    'is_custom_module',
                ];
                for (const key of PANEL_SEED_KEYS) {
                    delete newSettings[key];
                }

                const store = useSolarStore.getState();
                const currentSettings =
                    store.roofSettingsById[pending.segment.id] ?? {};

                store.updateRoofSetting(
                    pending.segment.id,
                    newSettings,
                    currentSettings,
                    ALURACK_NON_GEOMETRY_KEYS,
                );

                pendingEditRef.current = null;
                return;
            }

            // Creation path — a new segment draw was requested.
            const pending = pendingDrawRef.current;
            if (!pending) return;

            const initialSettings = _newSegmentConfigToInitialSettings(
                newSegmentConfig,
                parentFieldSegment,
            );

            // Alurack uses numeric POLYGON_* constants for drawingMode, but the
            // library expects a string tag ('rectangle' or 'free').
            const libraryDrawingMode =
                Number(drawingMode) === POLYGON_RECTANGLE
                    ? 'rectangle'
                    : 'free';

            pending.startDraw({
                initialSettings,
                drawingMode: libraryDrawingMode,
            });
            pendingDrawRef.current = null;
        },
        [parentFieldSegment],
    );

    const handlePreDrawFormCancel = useCallback(() => {
        pendingDrawRef.current = null;
        pendingEditRef.current = null;
        setAddSegmentModalEditionMode({
            isEditionMode: false,
            editionData: {},
        });
        setDrawingMode(false);
    }, [setDrawingMode, setAddSegmentModalEditionMode]);

    // ---- Widget callbacks ----------------------------------------------

    /**
     * For rectangle roofs, compute the building heading (water-drainage
     * direction) from the polygon geometry and sync it to the library store
     * as both `azimuthDeg` (used by panelLayout and ASCE calc) and
     * `building_heading` (round-trips through custom_inputs for legacy compat).
     *
     * Legacy behaviour: azimuth = getPolygonHeadings(polygon)[2]?.heading
     * The [2] index is the third edge — the long-axis facing direction.
     *
     * Returns the fresh settings from the store after the update (or the
     * unchanged settings if the segment is not a rectangle or Google Maps
     * is not yet loaded).
     */
    /**
     * Compute the building heading from the rectangle's polygon ring and
     * sync it into the library store.
     *
     * @param {boolean} initAzimuth  When true (segment just drawn) also sets
     *   `azimuthDeg` so panels start oriented correctly.  On subsequent saves
     *   we must NOT touch `azimuthDeg` — the user may have changed it via the
     *   right-click menu or the form, and overwriting it would silently discard
     *   that edit before the payload is sent to the backend.
     *   We DO always update `building_heading` so the ASCE calculation uses
     *   the current polygon direction even after the user rotates the panels.
     */
    const _syncRectangleHeading = useCallback(
        (segmentId, ring, currentSettings, { initAzimuth = false } = {}) => {
            if (!ring?.length || !window.google?.maps) return currentSettings;
            const polygonCoords = ring.map((p) => [p.lat, p.lng]);
            const headings = getPolygonHeadings({
                google: window.google,
                polygonCoords,
            });
            const heading = headings[2]?.heading;
            if (heading == null) return currentSettings;

            const patch = { building_heading: heading };
            if (initAzimuth && currentSettings?.azimuthDeg !== heading) {
                patch.azimuthDeg = heading;
            }
            // Nothing to write — skip to avoid triggering unnecessary re-layouts.
            if (Object.keys(patch).length === 0) return currentSettings;

            useSolarStore
                .getState()
                .updateRoofSetting(
                    segmentId,
                    patch,
                    currentSettings ?? {},
                    ALURACK_NON_GEOMETRY_KEYS,
                );
            return (
                useSolarStore.getState().roofSettingsById[segmentId] ??
                currentSettings
            );
        },
        [],
    );

    const onSegmentCreate = useCallback(
        (segment, { getUpdatedPayload } = {}) => {
            const store = useSolarStore.getState();
            let settings = store?.roofSettingsById?.[segment.id];
            if (segment.isRectangle) {
                // initAzimuth: true — first draw, orient panels to building heading.
                settings = _syncRectangleHeading(
                    segment.id,
                    segment.ring,
                    settings,
                    { initAzimuth: true },
                );
            }

            const hasName = Boolean(
                (settings?.name && settings.name.trim()) ||
                    (segment?.name && segment.name.trim()),
            );

            let effectiveSettings = settings ?? {};
            // For rectangle roofs the initial segment was computed BEFORE
            // _syncRectangleHeading updated azimuthDeg, so panels_layout is
            // oriented to the wrong azimuth. getUpdatedPayload() rebuilds it
            // using the now-correct store state so grouped_cells are correct.
            let effectiveSegment =
                segment.isRectangle && getUpdatedPayload
                    ? getUpdatedPayload()
                    : segment;

            if (!hasName && segment?.type === 'roof') {
                const nextSegmentNumber =
                    segmentsRef.current.filter(
                        (item) =>
                            item.type === POLYGON_SEGMENT ||
                            item.type === POLYGON_RECTANGLE,
                    ).length + 1;
                const generatedName = `${i18next.t('Segment')} ${nextSegmentNumber}`;
                effectiveSettings = {
                    ...(settings || {}),
                    name: generatedName,
                };
                effectiveSegment = { ...effectiveSegment, name: generatedName };
            }

            if (!hasName && segment?.type === 'obstacle') {
                const nextObstacleNumber =
                    segmentsRef.current.filter(
                        (item) => item.type === POLYGON_OBSTACLE,
                    ).length + 1;
                const generatedName = `${i18next.t('Obstacle')} ${nextObstacleNumber}`;
                effectiveSegment = { ...effectiveSegment, name: generatedName };
            }

            // Find the original API segment (if this is an update — for a
            // new draw it won't exist yet) so libraryToAlurack can preserve
            // server-owned fields like top-level `height`.
            const originalSegment = segmentsRef.current.find(
                (s) => s.id === segment.id,
            );

            const payload = librarySegmentToAlurack({
                segment: effectiveSegment,
                settings: effectiveSettings,
                context: { ...context, originalSegment },
            });

            const tempId = segment.id;
            const type = segment.type;

            apiCreateSegment(payload)
                .then((response) => {
                    const backendId =
                        response?.data?.id ?? response?.data?.data?.id;
                    if (backendId && backendId !== tempId) {
                        const currentStore = useSolarStore.getState();
                        if (type === 'roof') {
                            currentStore.renameRoof?.(tempId, backendId);
                            currentStore.setSelectedSegmentId?.((cur) =>
                                cur === tempId ? backendId : cur,
                            );
                        } else if (type === 'obstacle') {
                            currentStore.renameSegment?.(tempId, backendId);
                            currentStore.setSelectedSegmentId?.((cur) =>
                                cur === tempId ? backendId : cur,
                            );
                        } else if (type === 'image') {
                            currentStore.renameImageOverlay?.(
                                tempId,
                                backendId,
                            );
                            currentStore.setSelectedImageId?.((cur) =>
                                cur === tempId ? backendId : cur,
                            );
                        }
                    }

                    const segmentData = response?.data ?? response?.data?.data;
                    if (segmentData) {
                        const newSeg = formatSegment(
                            segmentData,
                            parentFieldSegmentId,
                        );
                        const updated = [...segmentsRef.current, newSeg];
                        segmentsRef.current = updated;
                        updateSegments(updated);
                    }
                })
                .catch((err) =>
                    console.error('[AlurackSolarPlanner] create failed:', err),
                );
        },
        [_syncRectangleHeading, context, parentFieldSegmentId, updateSegments],
    );

    const onSegmentUpdate = useCallback(
        (segment) => {
            const store = useSolarStore.getState();
            let settings = store?.roofSettingsById?.[segment.id];
            if (segment.isRectangle) {
                settings = _syncRectangleHeading(
                    segment.id,
                    segment.ring,
                    settings,
                );
            }

            // Carry the original API segment so server-owned fields
            // (top-level `height`, etc.) round-trip unchanged.
            const originalSegment = segmentsRef.current.find(
                (s) => s.id === segment.id,
            );
            const payload = librarySegmentToAlurack({
                segment,
                settings,
                context: { ...context, originalSegment },
            });

            apiUpdateSegment(segment.id, payload)
                .then((response) => {
                    const segmentData = response?.data ?? response?.data?.data;
                    const panelModules = segmentData?.panel_modules;
                    if (
                        segment.type === 'roof' &&
                        Array.isArray(panelModules)
                    ) {
                        const newModules = getNewSolarModules(panelModules);
                        segmentsRef.current = segmentsRef.current.map((s) =>
                            s.id === segment.id
                                ? { ...s, solar_modules: newModules }
                                : s,
                        );
                    }

                    if (segmentData) {
                        const updatedSeg = formatSegment(
                            segmentData,
                            parentFieldSegmentId,
                        );
                        const updated = segmentsRef.current.map((s) =>
                            s.id === segment.id ? { ...s, ...updatedSeg } : s,
                        );
                        segmentsRef.current = updated;
                        updateSegments(updated);
                    }
                })
                .catch((err) =>
                    console.error('[AlurackSolarPlanner] update failed:', err),
                );
        },
        [_syncRectangleHeading, context, parentFieldSegmentId, updateSegments],
    );

    const onSegmentDelete = useCallback(
        (id) => {
            apiDeleteSegment(id)
                .then(() => {
                    const updated = segmentsRef.current.filter(
                        (s) => s.id !== id,
                    );
                    segmentsRef.current = updated;
                    updateSegments(updated);
                })
                .catch((err) =>
                    console.error('[AlurackSolarPlanner] delete failed:', err),
                );
        },
        [updateSegments],
    );

    const onPanelToggle = useCallback(
        (toggles) => {
            const segMap = buildSegmentsMap(segmentsRef.current);
            const mapped = libraryPanelTogglesToAlurack(toggles, segMap);

            // Group by segment so we issue one patchGroup per segment.
            const bySegment = {};
            for (const item of mapped) {
                if (!bySegment[item.parentId]) bySegment[item.parentId] = [];
                bySegment[item.parentId].push(item);
            }

            for (const [segmentId, items] of Object.entries(bySegment)) {
                // 1. Patch each panel-module status.
                const patchPromises = items.map(({ moduleId, isEnabled }) =>
                    patchItem(moduleId, {
                        is_enable: isEnabled,
                        is_hidden: false,
                    }),
                );

                // 2. After all patches succeed, update store + call patchGroup
                //    — mirrors the legacy handleOnClickModule.js flow exactly.
                Promise.all(patchPromises)
                    .then(() => {
                        const segment = segmentsRef.current.find(
                            (s) => s.id === segmentId,
                        );
                        if (!segment?.grouped_cells?.length) return;

                        // Build enable-status lookup: moduleId → isEnabled.
                        const enableMap = new Map(
                            items.map(({ moduleId, isEnabled }) => [
                                moduleId,
                                isEnabled,
                            ]),
                        );

                        // Deep-copy grouped_cells and apply new enable flags.
                        const updatedGroupedCells = JSON.parse(
                            JSON.stringify(segment.grouped_cells),
                        );
                        for (const mod of segment.solar_modules ?? []) {
                            if (!enableMap.has(mod.id)) continue;
                            const { group, cell, row, col } = mod;
                            if (
                                updatedGroupedCells[group]?.[cell]?.[row]?.[
                                    col
                                ] != null
                            ) {
                                updatedGroupedCells[group][cell][row][col] = {
                                    ...updatedGroupedCells[group][cell][row][
                                        col
                                    ],
                                    enable: enableMap.get(mod.id),
                                };
                            }
                        }

                        // Update solar_modules enabled flag locally.
                        const updatedSolarModules = (
                            segment.solar_modules ?? []
                        ).map((mod) =>
                            enableMap.has(mod.id)
                                ? { ...mod, enabled: enableMap.get(mod.id) }
                                : mod,
                        );

                        const modulesGroups =
                            getModulesGroupV2(updatedGroupedCells);
                        const groups = prepareModulesGroups(modulesGroups);

                        // 3. Update the local store immediately (same as legacy).
                        const updatedSegments = segmentsRef.current.map((s) =>
                            s.id === segmentId
                                ? {
                                      ...s,
                                      grouped_cells: updatedGroupedCells,
                                      groups,
                                      modules_groups: modulesGroups,
                                      solar_modules: updatedSolarModules,
                                  }
                                : s,
                        );
                        segmentsRef.current = updatedSegments;
                        updateSegments(updatedSegments);

                        // 4. Compute ASCE wind-zone metadata and sync to
                        //    backend — mirrors handleOnClickModule.js logic.
                        let asceMetadata = [];
                        const isRect =
                            segment?.drawing_type === POLYGON_RECTANGLE ||
                            segment?.isRectangle;
                        if (isRect && window.google?.maps?.geometry) {
                            try {
                                const markedEdges =
                                    markGroupEdges(modulesGroups);
                                asceMetadata = setCeilZone({
                                    buildingHeight:
                                        segment.building_height ?? 3,
                                    ceilTilt: segment.ceil_tilt ?? 0,
                                    google: window.google,
                                    groups: markedEdges,
                                    heading:
                                        segment.building_heading ??
                                        segment.azimuth ??
                                        180,
                                    polygon: segment.polygon,
                                    sloofType: segment.sloof_type ?? null,
                                });
                            } catch (_err) {
                                // Ignore — geometry library may not be ready
                            }
                        }
                        patchGroup(segmentId, {
                            groups,
                            asce_metadata: asceMetadata,
                        }).catch((err) =>
                            console.error(
                                '[AlurackSolarPlanner] patchGroup failed:',
                                err,
                            ),
                        );
                    })
                    .catch((err) =>
                        console.error(
                            '[AlurackSolarPlanner] panel toggle failed:',
                            err,
                        ),
                    );
            }
        },
        [updateSegments],
    );

    const onSelectionChange = useCallback(() => {
        // Alurack does not mirror widget-side selection into Redux.
    }, []);

    // ---- roofEditExtra --------------------------------------------------
    // The library calls this render prop with `{ updateExtraSetting, selectedSettings }`.
    // We render Alurack's extra-field UI and rely on the widget to echo those
    // values back through onSegmentUpdate → merged into custom_inputs.
    const [selectedRectangleId, setSelectedRectangleId] = useState(null);

    useEffect(() => {
        const updateIsSelectedRectangle = (state) => {
            const seg = state.segments?.find(
                (r) => r.id === state.selectedSegmentId,
            );
            setSelectedRectangleId(seg?.isRectangle ?? false);
        };

        // Initial state
        updateIsSelectedRectangle(useSolarStore.getState());

        // Subscribe to changes
        const unsubscribe = useSolarStore.subscribe((state) => {
            updateIsSelectedRectangle(state);
        });

        return () => unsubscribe();
    }, []);

    // ---- Global system_name + system (id) backfill ---------------------
    // Legacy roofs saved before the host forwarded these fields end up with
    // either `system_name` or `system` (id) missing. The backend rejects
    // updates with an empty `system` (UUID validation), so we MUST resolve
    // both before the user saves. Strategy:
    //   1. Subscribe to the library store to detect when initialProject
    //      finishes hydrating (segments + roofSettingsById appear).
    //   2. Group missing roofs by `type_application` (one backend call
    //      covers all segments sharing that type).
    //   3. Look up each roof in the response by `system_uid` and patch
    //      whatever's missing (`system` and/or `system_name`) via
    //      `updateRoofSetting`.
    //
    // A ref tracks already-fetched type_applications and per-roof writes so
    // store updates don't cause re-fetching or re-writing.
    useEffect(() => {
        const fetchedTypes = new Set();
        const writtenIds = new Set();
        let cancelled = false;

        const runBackfill = (state) => {
            if (cancelled) return;
            const segs = state.segments || [];
            const settingsById = state.roofSettingsById || {};

            const byType = new Map();
            for (const seg of segs) {
                if (seg.type !== 'roof') continue;
                if (writtenIds.has(seg.id)) continue;
                const settings = settingsById[seg.id];
                if (!settings) continue;
                // Skip when both fields are already populated.
                if (settings.system_name && settings.system) {
                    writtenIds.add(seg.id);
                    continue;
                }
                const typeApp = settings.type_application;
                if (!typeApp) continue;
                const uidOrId = settings.system_uid || settings.system;
                if (!uidOrId) continue;
                if (fetchedTypes.has(typeApp)) continue;
                if (!byType.has(typeApp)) byType.set(typeApp, []);
                byType.get(typeApp).push({
                    id: seg.id,
                    system_uid: settings.system_uid,
                    system: settings.system,
                });
            }
            if (byType.size === 0) return;

            for (const [typeApp, roofs] of byType.entries()) {
                fetchedTypes.add(typeApp);
                filterSystemTypesList(typeApp)
                    .then((response) => {
                        if (cancelled) return;
                        const data = response?.data?.data || [];
                        if (data.length === 0) return;
                        const currentStore = useSolarStore.getState();
                        for (const roof of roofs) {
                            const match =
                                (roof.system_uid &&
                                    data.find(
                                        (s) => s?.uniqueid === roof.system_uid,
                                    )) ||
                                (roof.system &&
                                    data.find((s) => s?.id === roof.system));
                            if (!match) continue;
                            const currentSettings =
                                currentStore.roofSettingsById?.[roof.id] ?? {};
                            // Build a patch with only the missing fields so
                            // we don't clobber anything already populated.
                            const patch = {};
                            if (!currentSettings.system_name && match.name) {
                                patch.system_name = match.name;
                            }
                            if (!currentSettings.system && match.id) {
                                patch.system = match.id;
                            }
                            if (Object.keys(patch).length === 0) {
                                writtenIds.add(roof.id);
                                continue;
                            }
                            currentStore.updateRoofSetting(
                                roof.id,
                                patch,
                                currentSettings,
                                ALURACK_NON_GEOMETRY_KEYS,
                            );
                            writtenIds.add(roof.id);
                        }
                    })
                    .catch(() => {
                        // Silent — allow a retry next time segments arrive.
                        fetchedTypes.delete(typeApp);
                    });
            }
        };

        // Run against current state and subscribe for late-arriving segments
        // (initialProject can hydrate slightly after mount).
        runBackfill(useSolarStore.getState());
        const unsubscribe = useSolarStore.subscribe((state) => {
            runBackfill(state);
        });

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, []);

    // ---- Per-brand panel offer cache ------------------------------------
    // The library calls `getPanelOffer(settings)` to populate the panel
    // dropdown for THIS roof. We cache the per-brand panel list locally so
    // multiple roofs with different brands all see their own catalogue
    // (Redux's panels selector only ever holds one brand at a time).
    //
    // panelsByBrandRef[brand] === undefined → not yet fetched
    //                            null       → fetch in flight
    //                            array      → fetched, library-shape panels
    const panelsByBrandRef = useRef({});
    // Bump this counter whenever a brand finishes loading to force the
    // SolarPlanner to re-render; the ref itself is mutable so React can't
    // observe the cache change.
    const [panelOfferRevision, setPanelOfferRevision] = useState(0);

    const getPanelOffer = useCallback(
        (settings) => {
            const brand = settings?.brandName;
            if (!brand || brand === 'custom') {
                // No brand selected → fall back to the proposal's offer so
                // existing roofs continue to show panels until the user
                // picks a brand.
                return libraryPanelOffer;
            }
            const cached = panelsByBrandRef.current[brand];
            if (Array.isArray(cached)) return cached;
            // Kick off a fetch the first time we see this brand. Re-renders
            // will pick up the result via the panelOfferRevision counter.
            if (cached === undefined) {
                panelsByBrandRef.current[brand] = null;
                filterPanelsList(brand)
                    .then((response) => {
                        const data = response?.data?.data || [];
                        panelsByBrandRef.current[brand] = data.map((p) => ({
                            depth: p.depth,
                            height: p.height,
                            name: p.name,
                            panel_company_branch_office: p.id,
                            quantity: p.quantity,
                            watts: p.watts ?? p.max_power,
                            width: p.width,
                        }));
                        setPanelOfferRevision((r) => r + 1);
                    })
                    .catch(() => {
                        // On failure mark as empty so we don't refetch in a
                        // tight loop. The user can re-select the brand to
                        // retry.
                        panelsByBrandRef.current[brand] = [];
                        setPanelOfferRevision((r) => r + 1);
                    });
            }
            return [];
        },
        [libraryPanelOffer, panelOfferRevision],
    );

    const roofEditExtra = useCallback(
        ({ updateExtraSetting, selectedSettings }) => (
            <I18nextProvider>
                <AlurackRoofEditExtra
                    isRectangle={selectedRectangleId}
                    selectedSettings={selectedSettings}
                    updateExtraSetting={updateExtraSetting}
                />
            </I18nextProvider>
        ),
        [selectedRectangleId],
    );

    // Orientation editing rule lifted verbatim from the legacy TabModule.
    // Only systems that use clamp/grafada-style mounting let the installer
    // flip orientation; ballasted (LASTRADA) and other railed systems lock it
    // because the racking geometry assumes a single orientation.
    const canEditOrientation = useCallback((settings) => {
        const editableSystemUids = [
            'ARCK-CUBIERTA GRAFADA',
            'ARCK-TEJA BARRO',
            'ARCK-TRAPEZOIDAL',
            'ARCK-TRAPEZOIDAL CLAMP',
            'ARCK-TRAPEZOIDAL ALT',
        ];
        return editableSystemUids.includes(settings?.system_uid);
    }, []);

    const panelTabExtra = useCallback(
        ({
            allowCustomPanel,
            onPanelSelect,
            panelOffer,
            selectedSettings,
            updateSelectSetting,
            updateSetting,
        }) => (
            <I18nextProvider>
                <AlurackPanelTabExtra
                    allowCustomPanel={allowCustomPanel}
                    onPanelSelect={onPanelSelect}
                    panelOffer={panelOffer}
                    selectedSettings={selectedSettings}
                    updateSelectSetting={updateSelectSetting}
                    updateSetting={updateSetting}
                />
            </I18nextProvider>
        ),
        [],
    );

    const locale = getIsoCodeCurrentLocale() === 'es' ? 'es' : 'en';

    const onImageUpload = useCallback(
        async (segId, file) => {
            // Step 1: upload file to S3.
            const response = await updateImage(segId, file);
            const s3Url = response?.data?.image ?? null;

            // Step 2: persist the S3 URL to the backend via the segments
            // endpoint. We build the payload manually because the library's
            // internal .then() bypasses the onSegmentUpdate hook wrapper.
            if (s3Url) {
                const store = useSolarStore.getState();
                const seg = store.segments.find(
                    (s) => s.id === segId && s.type === 'image',
                );
                if (seg) {
                    const originalSegment = segmentsRef.current.find(
                        (s) => s.id === segId,
                    );
                    const payload = librarySegmentToAlurack({
                        segment: { ...seg, imageUrl: s3Url },
                        settings: undefined,
                        context: { ...context, originalSegment },
                    });
                    apiUpdateSegment(segId, payload).catch((err) =>
                        console.error(
                            '[AlurackSolarPlanner] image segment update failed:',
                            err,
                        ),
                    );
                }
            }

            return s3Url;
        },
        [context],
    );

    // -------------------------------------------------------------------

    return (
        <>
            <SolarPlanner
                canEditOrientation={canEditOrientation}
                getPanelOffer={getPanelOffer}
                googleApiKey={import.meta.env.VITE_GOOGLE_MAPS_KEY}
                initialProject={initialProject}
                isDisabledEdition={isDisabledEdition}
                locale={locale}
                onImageUpload={onImageUpload}
                onPanelToggle={onPanelToggle}
                onSegmentCreate={onSegmentCreate}
                onSegmentDelete={onSegmentDelete}
                onSegmentDrawRequest={onSegmentDrawRequest}
                onSegmentEditRequest={onSegmentEditRequest}
                onSegmentUpdate={onSegmentUpdate}
                onSelectionChange={onSelectionChange}
                panelOffer={libraryPanelOffer}
                panelTabExtra={panelTabExtra}
                roofEditExtra={roofEditExtra}
                viewMode={viewMode}
            />

            {/* Alurack's existing <AddSegmentFormContainer /> lives in
                Container.jsx. The bridge subscribes to its Redux actions to
                relay the submitted config to the widget. */}
            <PreDrawFormBridge
                isEdition={isEditionModeActive}
                onCancel={handlePreDrawFormCancel}
                onSubmit={handlePreDrawFormSubmit}
            />
        </>
    );
}

/**
 * Translate Alurack's newSegmentConfig (produced by AddSegmentFormContainer)
 * into the library's `initialSettings` shape.
 *
 * - building_height  → buildingHeight
 * - ceil_tilt        → roofSlopeDeg
 * - fill_algorithm   → panelFillMode + targetPanelCount / targetKw
 * - quantity         → target* based on panelFillMode
 * Everything else is passed through verbatim so the library echoes it back
 * into `custom_inputs` on save.
 */
function _newSegmentConfigToInitialSettings(
    newSegmentConfig,
    parentFieldSegment,
) {
    if (!newSegmentConfig) return {};

    const {
        building_height,
        category,
        ceil_tilt,
        fill_algorithm,
        quantity,
        type_application,
        system,
        system_uid,
        system_name,
        sloof_type,
        gclamp_type,
        preset_configuration,
        rail_spacing,
        rail_support_length,
        plate_support_length,
        beam_spacing,
        support_length,
        lfoot_supports_width,
        outer_rail_length,
        front_base_height,
        seam_distance,
        sheet_gauge,
    } = newSegmentConfig;

    let panelFillMode = 'fill';
    if (fill_algorithm === 'u') panelFillMode = 'units';
    else if (fill_algorithm === 'kW') panelFillMode = 'kw';
    else if (fill_algorithm === 'fill') panelFillMode = 'fill';

    const targetValues = {};
    if (panelFillMode === 'units' && Number.isFinite(Number(quantity))) {
        targetValues.targetPanelCount = Number(quantity);
    } else if (panelFillMode === 'kw' && Number.isFinite(Number(quantity))) {
        targetValues.targetKw = Number(quantity);
    }

    const defaultPanel = parentFieldSegment?.proposal?.panel;

    // Derive panel orientation from the selected system: ballasted systems
    // (LASTRADA) require horizontal modules, every other system defaults to
    // vertical (portrait). Matches the 'L' / 'P' convention the backend uses
    // and what alurackToLibrary applies when loading saved segments — so the
    // orientation is correct from the very first render, not only after
    // save + reload.
    const systemOrientation = getOrientationViaSystemUid(system_uid);
    const panelOrientation =
        systemOrientation === 'L' ? 'horizontal' : 'vertical';

    // Ballasted double-incline (ARCK-LASTRADA DOBLE) lays panels as
    // back-to-back pairs meeting at the ridge. The library consumes
    // `panelPairingMode === 'back_to_back'` to drop the ridge setback so
    // both halves can place panels flush against the apex. Every other
    // system leaves it unset (default non-paired layout).
    const normalizedSystemUid = String(system_uid || '')
        .replace(/^ARCK-/, '')
        .trim()
        .toUpperCase();
    const panelPairingMode =
        normalizedSystemUid === 'LASTRADA DOBLE'
            ? 'back_to_back'
            : normalizedSystemUid === 'LASTRADA SENCILLA'
              ? 'single_incline'
              : undefined;

    // Flat-roof systems always have roofSlopeDeg = 0.  ceil_tilt stores the
    // panel inclination for these systems, not the roof slope — using it as
    // roofSlopeDeg would stagger panel groups at different heights (staircase).
    // Also clear sloof_type so the library does not tilt the roof surface.
    const _flatRoofUids = [
        'ARCK-GRANJA LOSA',
        'ARCK-LASTRADA DOBLE',
        'ARCK-LASTRADA SENCILLA',
        'ARCK-LOSA CONVENCIONAL',
    ];
    const _isFlatRoof =
        _flatRoofUids.includes(system_uid) ||
        _flatRoofUids.includes(`ARCK-${normalizedSystemUid}`);
    const resolvedRoofSlopeDeg = _isFlatRoof ? 0 : Number(ceil_tilt) || 0;
    const resolvedSloofType = _isFlatRoof ? undefined : sloof_type;

    // Seed the panel-tab fields from the proposal's default panel so the
    // Module tab renders with brand + model + dimensions populated from the
    // very first mount (not only after a save+reload). Mirrors what
    // alurackToLibrary writes when loading existing segments.
    const defaultPanelSeeds = defaultPanel?.id
        ? {
              selectedPanelCode: defaultPanel.id,
              brandName: defaultPanel.brand?.name ?? '',
              panel_name: defaultPanel.name ?? '',
              panelWidth: Number.isFinite(Number(defaultPanel.width))
                  ? Number(defaultPanel.width) / 1000
                  : undefined,
              panelHeight: Number.isFinite(Number(defaultPanel.height))
                  ? Number(defaultPanel.height) / 1000
                  : undefined,
              panelDepth: Number.isFinite(Number(defaultPanel.depth))
                  ? Number(defaultPanel.depth) / 1000
                  : undefined,
              panelWatts:
                  defaultPanel.watts ?? defaultPanel.max_power ?? undefined,
              panel_weight:
                  defaultPanel.weight != null
                      ? Number(defaultPanel.weight)
                      : undefined,
              is_custom_module: defaultPanel.is_custom === true,
          }
        : {};

    return {
        // Library-known settings
        buildingHeight: Number(building_height) || 0,
        roofSlopeDeg: resolvedRoofSlopeDeg,
        panelFillMode,
        panelOrientation,
        ...(panelPairingMode ? { panelPairingMode } : {}),
        ...targetValues,
        ...defaultPanelSeeds,
        type_application,
        system,
        system_uid,
        system_name,
        category,
        sloof_type: resolvedSloofType,
        gclamp_type,
        preset_configuration,
        rail_spacing,
        rail_support_length,
        plate_support_length,
        beam_spacing,
        support_length,
        lfoot_supports_width,
        outer_rail_length,
        front_base_height,
        seam_distance,
        sheet_gauge,
        fill_algorithm,
    };
}

AlurackSolarPlanner.propTypes = {
    isDisabledEdition: PropTypes.bool,
    isEdition: PropTypes.object,
    match: PropTypes.object,
    offerPanels: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    parentFieldSegment: PropTypes.object,
    presetConfiguration: PropTypes.object,
    segments: PropTypes.array,
    setAddSegmentModalEditionMode: PropTypes.func,
    setAddSegmentModalOpenStatus: PropTypes.func,
    setDrawingMode: PropTypes.func,
    updateSegments: PropTypes.func,
    viewMode: PropTypes.oneOf(['2d', '3d']),
};

const mapStateToProps = createStructuredSelector({
    isEdition: selectors.getAddSegmentModalEditionMode,
    offerPanels: selectors.getOfferPanelsData,
    parentFieldSegment: selectors.getParentFieldSegmentData,
    presetConfiguration: selectors.getPresetConfiguration,
    segments: selectors.getSegmentsData,
});

const mapDispatchToProps = (dispatch) => ({
    setAddSegmentModalOpenStatus: (isOpen) =>
        dispatch(actions.setAddSegmentModalOpenStatus(isOpen)),
    setAddSegmentModalEditionMode: (isEdition) =>
        dispatch(actions.setAddSegmentModalEditionMode(isEdition)),
    setDrawingMode: (isDrawing, type) =>
        dispatch(actions.setDrawingMode(isDrawing, type)),
    updateSegments: (values) => dispatch(actions.updateSegments(values)),
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withRouter,
)(AlurackSolarPlanner);

// Named export for consumers that manage Redux/router themselves
export { AlurackSolarPlanner as AlurackSolarPlannerPlain };

// Also re-export placeholders for ergonomics
export const DEFAULT_SEGMENT_COLOR = DEFAULT_COLOR;
export const DEFAULT_SEGMENT_STROKE = DEFAULT_STROKE;
